<template>
  <div
    v-if="isPromotionalBannersLoading"
    class="animate-pulse"
  >
    <div class="h-40 w-full rounded-md bg-gray-200 md:h-48 lg:h-64"></div>
  </div>

  <div
    v-else-if="promotionalBanners?.length"
    class="group relative overflow-hidden rounded-md"
    @mouseenter="pauseAutoRotate"
    @mouseleave="resumeAutoRotate"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div
      class="flex h-full transition-transform duration-700 ease-in-out will-change-transform"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <promotional-banner
        v-for="banner in promotionalBanners"
        :key="banner.id"
        :banner="banner"
        class="size-full shrink-0"
        @click="handleBannerClick"
      />
    </div>

    <!-- Navigation Dots (only show if multiple banners) -->
    <div
      v-if="promotionalBanners.length > 1 && showIndicators"
      class="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-2"
    >
      <button
        v-for="(banner, index) in promotionalBanners"
        :key="banner.id"
        class="size-2 rounded-full transition-all duration-300 ease-in-out"
        :class="index === currentIndex ? 'scale-110 bg-white' : 'bg-white/50 hover:scale-105 hover:bg-white/75'"
        :aria-label="`Go to slide ${index + 1}`"
        @click="goToSlide(index)"
      />
    </div>

    <!-- Navigation Arrows (only show if multiple banners) -->
    <template v-if="promotionalBanners.length > 1 && showArrows">
      <button
        class="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/20 p-2 text-white opacity-0 transition-all duration-300 ease-in-out hover:scale-110 hover:bg-black/40 focus:opacity-100 focus:outline-none group-hover:opacity-100"
        :aria-label="'Previous banner'"
        @click="previousSlide"
      >
        <ChevronLeftIcon class="size-6" />
      </button>

      <button
        class="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/20 p-2 text-white opacity-0 transition-all duration-300 ease-in-out hover:scale-110 hover:bg-black/40 focus:opacity-100 focus:outline-none group-hover:opacity-100"
        :aria-label="'Next banner'"
        @click="nextSlide"
      >
        <ChevronRightIcon class="size-6" />
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import usePromotionalBannersQuery from './queries/promotional-banners-query';
import PromotionalBanner from './promotional-banner.vue';

interface PromotionalBannersCarouselProps {
  targetPage?: 'everywhere' | 'landing' | 'shopLanding' | 'makers' | 'products';
  autoRotate?: boolean;
  rotationInterval?: number;
  showIndicators?: boolean;
  showArrows?: boolean;
  height?: 'small' | 'medium' | 'large' | 'xlarge';
  className?: string;
}

const props = withDefaults(defineProps<PromotionalBannersCarouselProps>(), {
  autoRotate: true,
  rotationInterval: 5000,
  showIndicators: true,
  showArrows: true,
  height: 'medium',
});

const emit = defineEmits<{
  bannerClick: [banner: PromotionalBanner];
}>();

const queryParams = computed(() => {
  return props.targetPage ? `target_page=${props.targetPage}` : '';
});

const { promotionalBanners, isPromotionalBannersLoading } = usePromotionalBannersQuery(queryParams);

// Carousel state
const currentIndex = ref(0);
const isPaused = ref(false);
const autoRotateInterval = ref<NodeJS.Timeout | null>(null);

// Touch/swipe handling
const touchStartX = ref(0);
const touchStartY = ref(0);
const isSwiping = ref(false);

// Navigation methods
function nextSlide() {
  if (!promotionalBanners.value?.length) return;
  currentIndex.value = (currentIndex.value + 1) % promotionalBanners.value.length;
}

function previousSlide() {
  if (!promotionalBanners.value?.length) return;
  currentIndex.value = currentIndex.value === 0 ? promotionalBanners.value.length - 1 : currentIndex.value - 1;
}

function goToSlide(index: number) {
  currentIndex.value = index;
}

function startAutoRotate() {
  if (props.autoRotate && promotionalBanners.value?.length > 1 && !isPaused.value) {
    autoRotateInterval.value = setInterval(() => {
      nextSlide();
    }, props.rotationInterval);
  }
}

function stopAutoRotate() {
  if (autoRotateInterval.value) {
    clearInterval(autoRotateInterval.value);
    autoRotateInterval.value = null;
  }
}

function pauseAutoRotate() {
  isPaused.value = true;
  stopAutoRotate();
}

function resumeAutoRotate() {
  isPaused.value = false;
  startAutoRotate();
}

// Touch/swipe handlers
function handleTouchStart(e: TouchEvent) {
  touchStartX.value = e.touches[0].clientX;
  touchStartY.value = e.touches[0].clientY;
  isSwiping.value = false;
  pauseAutoRotate();
}

function handleTouchMove(e: TouchEvent) {
  if (!isSwiping.value) {
    const deltaX = Math.abs(e.touches[0].clientX - touchStartX.value);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY.value);

    // Determine if this is a horizontal swipe
    if (deltaX > deltaY && deltaX > 10) {
      isSwiping.value = true;
      e.preventDefault(); // Prevent scrolling when swiping
    }
  }
}

function handleTouchEnd(e: TouchEvent) {
  if (isSwiping.value) {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartX.value - touchEndX;

    // Minimum swipe distance for smooth UX
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        nextSlide();
      } else {
        previousSlide();
      }
    }
  }

  isSwiping.value = false;
  resumeAutoRotate();
}

// Banner click handler
function handleBannerClick(banner: PromotionalBanner) {
  emit('bannerClick', banner);
}

// Lifecycle
onMounted(() => {
  startAutoRotate();
});

onUnmounted(() => {
  stopAutoRotate();
});

watch(promotionalBanners, () => {
  currentIndex.value = 0;
  stopAutoRotate();
  startAutoRotate();
});
</script>
